<template>
    <div>
        <div v-if="showLoading" class="my-6">
            <p>Fetching information. Please wait...</p>
        </div>
        <div v-if="showError" class="my-6">
            <p>There was a problem while processing Myinfo registration request.</p>
            <p>Please fill in
                <router-link :to="'/registration/basic-info'" class="text-primary-500 hover:text-primary-600">
                    registration forms
                </router-link>
                manually.
            </p>
        </div>
        <form autocomplete="off" v-if="!showError && !showLoading">
            <div class="grid md:grid-cols-2 gap-3">
                <k-input
                    class="mb-4"
                    id="name"
                    label="Name"
                    v-model="form.name"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="marriedName"
                    label="Married name"
                    v-model="form.marriedName"
                    disabled/>
            </div>
            <div class="grid md:grid-cols-3 gap-3">
                <k-input
                    class="mb-4"
                    id="sex"
                    label="Sex"
                    v-model="form.sex"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="dob"
                    label="Date of birth"
                    v-model="form.dob"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="uinFin"
                    label="NRIC/FIN"
                    v-model="form.uinFin"
                    disabled/>
            </div>
            <div class="grid md:grid-cols-3 gap-3">
                <k-input
                    class="mb-4"
                    id="birthCountry"
                    label="Birth country"
                    v-model="form.birthCountry"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="nationality"
                    label="Nationality"
                    v-model="form.nationality"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="residentialStatus"
                    label="Residential status"
                    v-model="form.residentialStatus"
                    disabled/>
            </div>
            <div class="grid md:grid-cols-2 gap-3">
                <k-input
                    class="mb-4"
                    id="passType"
                    label="Pass type"
                    v-model="form.passType"
                    disabled/>
                <k-input
                    class="mb-4"
                    id="passExpiryDate"
                    label="Pass expiration date"
                    v-model="form.passExpiryDate"
                    disabled/>
            </div>
            <div class="mb-4">
                <k-input
                    class="mb-4"
                    id="regAdd"
                    label="Registered address"
                    v-model="form.regAdd"
                    disabled/>
            </div>
            <div class="mb-4">
                <k-input
                    class="mb-4"
                    id="noaBasic"
                    label="IRAS Assessable Income (Latest Year)"
                    v-model="form.noaBasic"
                    disabled/>
            </div>
            <div class="mb-4">
                <label class="block text-gray-700 text-sm font-bold mb-2" for="mobilePhone">
                    {{ $t('mobilePhone') }}
                </label>
                <vue-tel-input v-model="form.mobileNo"
                               :validCharactersOnly="true"
                               inputClasses="appearance-none block w-full bg-gray-200 border-gray-200 text-gray-700 border rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                               :enabledCountryCode="false"
                               inputId="mobilePhone"
                               mode="international"
                               :preferredCountries="['SG']"
                               placeholder=""
                               :wrapperClasses="['appearance-none block w-full bg-gray-200 text-gray-700 border rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500', fieldError('mobilePhone') ? 'border-red-500!': 'border-gray-200']">

                </vue-tel-input>
                <div v-if="fieldError('mobilePhone')"
                     class="text-red-500 text-xs italic">
                    {{ fieldError('mobilePhone') }}
                </div>
            </div>
            <k-input
                class="mb-4"
                id="email"
                label="Email"
                v-model="form.email"
                :field-errors="validation.fieldErrors"
                type="email"/>
            <div class="grid md:grid-cols-2 gap-3" v-if="!hidePassword">
                <k-input
                    class="mb-4"
                    id="password"
                    label="Password"
                    v-model="form.password"
                    :field-errors="validation.fieldErrors"
                    type="password"/>
                <k-input
                    class="mb-4"
                    id="passwordRepeat"
                    label="Confirm password"
                    v-model="form.passwordRepeat"
                    :field-errors="validation.fieldErrors"
                    type="password"/>
            </div>
            <div class="grid md:grid-cols-2 gap-3">
                <k-input
                    class="mb-4"
                    id="referralCode"
                    label="Referral code"
                    v-model="form.referralCode"
                    :field-errors="validation.fieldErrors"/>
            </div>
            <div>
                <a-checkbox
                    class="mb-4"
                    id="acceptTermsOfUse"
                    v-model="form.acceptTermsOfUse"
                >{{ $t('iAccept') }}
                </a-checkbox>
                <a href="https://assets.website-files.com/61c1fc0c4b29a848ee7d2928/633149af2ffcde097e5b8fba_20220815_Terms%20of%20Use.pdf"
                   target="_blank"
                   class="text-primary-500">{{ $t('terms') }}</a>
                <div v-if="validation.fieldErrors['acceptTermsOfUse']" class="text-red-500 text-xs italic">
                    {{ validation.fieldErrors['acceptTermsOfUse'] }}
                </div>
            </div>
            <div>
                <a-checkbox
                    class="mb-4"
                    id="acceptPrivacyPolicy"
                    v-model="form.acceptPrivacyPolicy">
                    {{ $t('iAccept') }}
                </a-checkbox>
                <a href="https://www.kilde.sg/privacy"
                   target="_blank"
                   class="text-primary-500">{{ $t('privacyPolicy') }}</a>
                <div v-if="validation.fieldErrors['acceptPrivacyPolicy']" class="text-red-500 text-xs italic">
                    {{ validation.fieldErrors['acceptPrivacyPolicy'] }}
                </div>
            </div>
            <div class="mt-6">
                <k-button @click.native.prevent="onSubmit">
                    {{ $t('createAccount') }}
                </k-button>
            </div>
        </form>
    </div>
</template>

<script>

import _ from 'lodash'
import {VueTelInput} from 'vue-tel-input'

export default {
    components: {
        VueTelInput,
    },
    props: {
        validation: {
            type: Object,
            required: true
        },
        hidePassword: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            landingUrl: process.env.VUE_APP_KILDE_LENDING_BASE_URL,
            showLoading: true,
            showError: false,
            myInfoReference: null,
            form: {
                uinFin: null,
                name: null,
                marriedName: null,
                sex: null,
                dob: null,
                residentialStatus: null,
                nationality: null,
                birthCountry: null,
                passType: null,
                passExpiryDate: null,
                mobileNo: null,
                regAdd: null,
                noaBasic: null,
                email: null,
                password: null,
                passwordRepeat: null,
                referralCode: null,
                acceptTermsOfUse: null,
                acceptPrivacyPolicy: null
            }
        }
    },
    async mounted () {
        await this.getMyInfoData()
    },
    methods: {
        async onSubmit () {
            this.$emit('submit', {
                myInfoReference: this.myInfoReference,
                mobilePhone: this.form.mobileNo,
                email: this.form.email,
                password: this.form.password,
                passwordRepeat: this.form.passwordRepeat,
                referralCode: this.form.referralCode,
                acceptTermsOfUse: this.form.acceptTermsOfUse,
                acceptPrivacyPolicy: this.form.acceptPrivacyPolicy
            })
        },
        fieldError (field) {
            return this.validation.fieldErrors[field]
        },
        async getMyInfoData () {
            try {
                const resp = await this.$http.get('/api/investor/public/my-info', {
                    params: {
                        code: this.$route.query.code,
                        state: this.$route.query.state
                    }
                })
                this.showLoading = false
                this.myInfoReference = resp.data.requestId
                const data = JSON.parse(resp.data.dataJson)
                if (data) {
                    let noaData = ''
                    let address = ''
                    if (data['noa-basic']) {
                        noaData = this.str(data['noa-basic'].amount) ? this.$options.filters.currencyFormat(this.str(data['noa-basic'].amount, 'S$')) : ''
                    }
                    if (data.regadd.type === 'SG') {
                        address = this.str(data.regadd.country) === '' ? '' :
                            this.str(data.regadd.block) + ' ' +
                            this.str(data.regadd.building) + ' \n' +
                            '#' + this.str(data.regadd.floor) + '-' + this.str(data.regadd.unit) + ' ' +
                            this.str(data.regadd.street) + ' \n' +
                            'Singapore ' + this.str(data.regadd.postal)
                    } else if (data.regadd.type === 'Unformatted') {
                        address = this.str(data.regadd.line1) + '\n' +
                            this.str(data.regadd.line2)
                    }

                    this.form.uinFin = this.value(data, 'uinfin')
                    this.form.name = this.value(data, 'name')
                    this.form.marriedName = this.value(data, 'marriedname')
                    this.form.sex = this.value(data, 'sex')
                    this.form.dob = this.value(data, 'dob')
                    this.form.residentialStatus = this.value(data, 'residentialstatus')
                    this.form.nationality = this.value(data, 'nationality')
                    this.form.birthCountry = this.value(data, 'birthcountry')
                    this.form.passType = this.value(data, 'passtype')
                    this.form.passExpiryDate = this.value(data, 'passexpirydate')
                    this.form.mobileNo = this.phoneNumber(data)
                    this.form.regAdd = address
                    this.form.noaBasic = noaData
                    this.form.email = this.value(data, 'email')
                }

            } catch (e) {
                this.showError = true
                this.showLoading = false
            }
        },
        str (data) {
            if (!data)
                return null
            if (data.value)
                return data.value
            else if (data.desc)
                return data.desc
            else if (typeof data == 'string')
                return data
            else
                return ''
        },
        value (data, fieldName) {
            if (data !== null) {
                const field = data[fieldName]
                if (field) {
                    if (field.value) {
                        return field.value
                    } else if (field.desc) {
                        return field.desc
                    }
                }
            }
            return '-'
        },
        phoneNumber (data) {
            return _.filter([
                data['mobileno'].prefix.value,
                data['mobileno'].areacode.value,
                data['mobileno'].nbr.value
            ]).join('')
        },
        address (data) {
            return _.filter([
                data['regadd'].block.value,
                data['regadd'].building.value,
                data['regadd'].floor.value,
                data['regadd'].unit.value,
                data['regadd'].street.value,
                data['regadd'].postal.value,
                data['regadd'].country.desc,
            ]).join(' ')
        }
    }
}
</script>

<style>
.vue-tel-input {
    border: unset !important;
}
</style>

